import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import supplyContinuousDocumentService from '../../services/supplyContinuousDocumentService';
import ProductSelector from '../../components/PurchaseOrders/ProductSelector';
import UnitSelect from '../../components/Common/UnitSelect';
import ServiceSelector from '../../components/ServiceOrders/ServiceSelector';
import MachineryEventModal from '../../components/Machinery/MachineryEventModal';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import PriceIgvHint from '../../components/Common/PriceIgvHint';
import PricesIncludeIgvToggle from '../../components/Common/PricesIncludeIgvToggle';
import UnitPriceTextInput from '../../components/Common/UnitPriceTextInput';
import QuantityTextInput from '../../components/Common/QuantityTextInput';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { getItemLineSubtotal } from '../../utils/unitSelectUtils';
import { getUnits } from '../../services/unitsService';
import { formatDateLocaleLima, formatDateToLima, formatTimeToLima, getCurrentDateLima, getCurrentTimeLima } from '../../utils/dateUtils';

const SupplyContinuousDocumentDetail = () => {
  const { productConversionsEnabled } = useModuleSettings();
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  
  // Estados para modales
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isEditItemModalOpen, setIsEditItemModalOpen] = useState(false);
  const [isDeleteItemModalOpen, setIsDeleteItemModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Estados para formulario de item
  const [itemForm, setItemForm] = useState({
    item_type: 'product',
    product_id: '',
    service_id: '',
    description: '',
    project_id: '',
    item_date: '', // Fecha del item
    item_time: '', // Hora del item
    quantity: 1,
    unit_price: 0,
    has_igv: true,
    unit_of_measure: '',
    is_direct_consumption: false,
    warehouse_id: '',
  });

  const [itemErrors, setItemErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [units, setUnits] = useState([]);
  const [machineryEventsByItemId, setMachineryEventsByItemId] = useState({});
  const [isMachineryEventModalOpen, setIsMachineryEventModalOpen] = useState(false);
  const [pendingMachineryEvent, setPendingMachineryEvent] = useState(null);
  const lastLoadedId = useRef(null);

  // Edición de cabeceras (Información General) y montos (solo suministro continuo)
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({
    issue_date: '',
    series: '',
    number: '',
    notes: '',
    base_imponible: '',
    igv_percentage: '18',
    no_gravados: '',
  });
  const [savingHeader, setSavingHeader] = useState(false);
  const [pricesIncludeIgv, setPricesIncludeIgv] = useState(false);

  // Montos calculados en edición (suministro continuo): IGV = Base × %/100 (%, 18, 10 o no gravado), Total = Base + IGV + No Gravados
  const baseNum = parseFloat(headerForm.base_imponible) || 0;
  const igvPctValue = headerForm.igv_percentage; // '18' | '10' | 'no_gravado'
  const igvPctNum = igvPctValue === 'no_gravado' ? 0 : (parseFloat(igvPctValue) || 0);
  const noGravadosNum = parseFloat(headerForm.no_gravados) || 0;
  const calculatedIgv = Math.round(baseNum * (igvPctNum / 100) * 100) / 100;
  const calculatedTotal = Math.round((baseNum + calculatedIgv + noGravadosNum) * 100) / 100;

  // Para vista (no edición): usar valores guardados en BD; si no existen, recalcular
  const displayBase = parseFloat(document?.base_imponible || 0);
  const displayNoGrav = parseFloat(document?.no_gravados || 0);
  const storedIgv = parseFloat(document?.igv_amount);
  const storedTotal = parseFloat(document?.total_amount);
  const displayIgvRate = document?.igv_rate != null ? Number(document.igv_rate) : 18;
  const calcIgv = Math.round(displayBase * (displayIgvRate / 100) * 100) / 100;
  const displayIgv = Number.isFinite(storedIgv) ? storedIgv : calcIgv;
  const calcTotal = Math.round((displayBase + displayIgv + displayNoGrav) * 100) / 100;
  const displayTotalDoc = Number.isFinite(storedTotal) ? storedTotal : calcTotal;

  useEffect(() => {
    // Solo cargar si el id cambió (evita doble llamada en StrictMode)
    if (lastLoadedId.current !== id) {
      lastLoadedId.current = id;
      loadDocument();
      loadFormData();
    }
  }, [id]);

  const loadFormData = async () => {
    try {
      const [productsRes, servicesRes, projectsRes, warehousesRes, unitsRes] = await Promise.all([
        supplyContinuousDocumentService.getProducts(),
        supplyContinuousDocumentService.getServices(),
        supplyContinuousDocumentService.getProjects(),
        supplyContinuousDocumentService.getWarehouses(),
        getUnits({ active_only: true }),
      ]);

      if (productsRes.success) setProducts(productsRes.data || []);
      if (servicesRes.success) setServices(servicesRes.data || []);
      if (projectsRes.success) setProjects(projectsRes.data || []);
      if (warehousesRes.success) setWarehouses(warehousesRes.data || []);
      if (unitsRes.success) setUnits(unitsRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
    }
  };

  const loadDocument = async () => {
    setLoading(true);
    try {
      const response = await supplyContinuousDocumentService.getDocument(id);
      if (response.success) {
        setDocument(response.data);
        setItems(response.data.items || []);
        const rawEvents = Array.isArray(response.data?.machinery_events)
          ? response.data.machinery_events
          : (Array.isArray(response.data?.machineryEvents) ? response.data.machineryEvents : []);
        const eventsByItem = rawEvents.reduce((acc, ev) => {
          if (ev?.document_item_id != null) {
            acc[ev.document_item_id] = ev;
          }
          return acc;
        }, {});
        setMachineryEventsByItemId(eventsByItem);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el documento',
          type: 'error'
        });
        setTimeout(() => navigate('/documents'), 2000);
      }
    } catch (error) {
      console.error('Error al cargar documento:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el documento',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getItemsTotal = () => {
    return items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
  };

  // Calcular solo subtotales de items (sin IGV) - base imponible + no gravados
  const getItemsSubtotal = () => {
    return items.reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);
  };

  const getPendingAmount = () => {
    if (!document) return 0;
    // Saldo pendiente = (base imponible + no gravados) - subtotales de items
    const baseImponible = parseFloat(document.base_imponible || 0);
    const noGravados = parseFloat(document.no_gravados || 0);
    const itemsSubtotal = getItemsSubtotal();
    const expectedTotal = baseImponible + noGravados;
    return Math.max(0, expectedTotal - itemsSubtotal);
  };

  const handleAddItem = () => {
    // Usar siempre la fecha y hora actuales al agregar un item
    const defaultDate = getCurrentDateLima();
    const defaultTime = getCurrentTimeLima();
    setItemForm({
      item_type: 'product',
      product_id: '',
      service_id: '',
      description: '',
      project_id: '',
      item_date: defaultDate,
      item_time: defaultTime,
      quantity: 1,
      unit_price: 0,
      has_igv: true,
      unit_of_measure: '',
      is_direct_consumption: false,
      warehouse_id: '',
    });
    setItemErrors({});
    setSelectedItem(null);
    setPendingMachineryEvent(null);
    setIsAddItemModalOpen(true);
  };

  const mapMachineryEventToModalForm = (ev) => {
    if (!ev) return null;
    const recordType = ev.machine_id ? 'machine' : 'vehicle';
    const eventTypeName = ev.event_type?.name || ev.eventType?.name || ev.event_type || '';
    const summaryParts = [];
    if (ev.machine) summaryParts.push(`Máquina: ${ev.machine.code || ''} - ${ev.machine.name || ''}`.trim());
    if (ev.vehicle) summaryParts.push(`Vehículo: ${ev.vehicle.code || ''} - ${ev.vehicle.name || ''}`.trim());
    if (ev.event_time) summaryParts.push(String(ev.event_time).substring(0, 5));
    if (ev.hours != null && ev.hours !== '') summaryParts.push(`${ev.hours} h`);
    if (ev.kilometers != null && ev.kilometers !== '') summaryParts.push(`${ev.kilometers} km`);
    if (ev.operator || ev.conductor?.name) summaryParts.push(ev.operator || ev.conductor?.name);
    if (eventTypeName) summaryParts.push(eventTypeName);
    return {
      event_date: ev.event_date || '',
      event_time: ev.event_time ? String(ev.event_time).substring(0, 5) : '',
      record_type: recordType,
      machine_id: ev.machine_id != null ? String(ev.machine_id) : '',
      vehicle_id: ev.vehicle_id != null ? String(ev.vehicle_id) : '',
      hours: ev.hours != null ? String(ev.hours) : '',
      kilometers: ev.kilometers != null ? String(ev.kilometers) : '',
      operator: ev.operator || ev.conductor?.name || '',
      event_type: eventTypeName,
      project_id: ev.project_id != null ? String(ev.project_id) : '',
      notes: ev.notes || '',
      displaySummary: summaryParts.filter(Boolean).join(' · '),
    };
  };

  const getMachinerySummaryByItem = (itemId) => {
    const ev = machineryEventsByItemId?.[itemId];
    if (!ev) return '';
    const mapped = mapMachineryEventToModalForm(ev);
    return mapped?.displaySummary || '';
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    // Usar la fecha del documento si el item no tiene fecha propia
    const defaultDate = document?.issue_date ? new Date(document.issue_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    
    // Extraer fecha y hora del item_date si existe
    let itemDate = defaultDate;
    let itemTime = '';
    if (item.item_date) {
      const dateObj = new Date(item.item_date);
      if (!isNaN(dateObj.getTime())) {
        itemDate = dateObj.toISOString().split('T')[0];
        itemTime = `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;
      }
    }
    if (!itemTime) {
      const now = new Date();
      itemTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    }
    
    setItemForm({
      item_type: item.item_type,
      product_id: item.product_id?.toString() || '',
      service_id: item.service_id?.toString() || '',
      description: item.description || '',
      project_id: item.project_id?.toString() || '',
      item_date: itemDate,
      item_time: itemTime,
      quantity: item.quantity,
      unit_price: item.unit_price,
      has_igv: item.has_igv !== false,
      unit_of_measure: item.unit_of_measure || '',
      is_direct_consumption: item.is_direct_consumption || false,
      warehouse_id: item.warehouse_id?.toString() || '',
    });
    setItemErrors({});
    const existingEvent = mapMachineryEventToModalForm(machineryEventsByItemId[item.id]);
    setPendingMachineryEvent(existingEvent);
    setIsEditItemModalOpen(true);
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setIsDeleteItemModalOpen(true);
  };

  const handleConfirmDeleteItem = async () => {
    if (!itemToDelete) return;

    setLoadingAction(true);
    try {
      const response = await supplyContinuousDocumentService.deleteItem(id, itemToDelete.id);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Item eliminado exitosamente',
          type: 'success'
        });
        setIsDeleteItemModalOpen(false);
        // Solo actualizar los items, no recargar todo el documento
        setItems(prevItems => prevItems.filter(item => item.id !== itemToDelete.id));
        setItemToDelete(null);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el item',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar item:', error);
      setToast({
        isOpen: true,
        message: 'Error al eliminar el item',
        type: 'error'
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const calculateItemTotals = (quantity, unitPrice, hasIgv, product = null, unitCode = '') => {
    const price = parseFloat(unitPrice || 0);
    const subtotal = getItemLineSubtotal({
      product,
      unitCode,
      quantity,
      unitPrice: price,
      productConversionsEnabled,
    });
    const rate = parseFloat(document?.igv_rate ?? 18);
    const taxRate = hasIgv ? rate : 0;
    const taxAmount = hasIgv ? subtotal * (rate === 10.5 ? 0.105 : rate === 10 ? 0.10 : 0.18) : 0;
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total, taxRate };
  };

  const handleSaveItem = async () => {
    const newErrors = {};

    if (itemForm.item_type === 'product' && !itemForm.product_id) {
      newErrors.product_id = 'El producto es requerido';
    }
    if (itemForm.item_type === 'service' && !itemForm.service_id) {
      newErrors.service_id = 'El servicio es requerido';
    }
    if (!itemForm.quantity || parseFloat(itemForm.quantity) <= 0) {
      newErrors.quantity = 'La cantidad debe ser mayor a 0';
    }
    if (!itemForm.unit_price || parseFloat(itemForm.unit_price) <= 0) {
      newErrors.unit_price = 'El precio unitario debe ser mayor a 0';
    }
    // Si es producto y no es consumo directo, el almacén es obligatorio
    if (itemForm.item_type === 'product' && !itemForm.is_direct_consumption && !itemForm.warehouse_id) {
      newErrors.warehouse_id = 'Debe seleccionar un almacén cuando no es consumo directo';
    }

    setItemErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const lineProduct =
      itemForm.item_type === 'product' && itemForm.product_id
        ? products.find((p) => String(p.id) === String(itemForm.product_id))
        : null;
    const { subtotal, taxAmount, total, taxRate } = calculateItemTotals(
      itemForm.quantity,
      itemForm.unit_price,
      itemForm.has_igv,
      lineProduct,
      itemForm.unit_of_measure
    );

    const pendingAmount = getPendingAmount();
    // Para la validación, usamos el subtotal (sin IGV) porque el saldo pendiente solo considera base imponible + no gravados
    const currentItemSubtotal = selectedItem ? parseFloat(selectedItem.subtotal || 0) : 0;
    const newItemSubtotal = subtotal - currentItemSubtotal;

    if (newItemSubtotal > pendingAmount + 0.01) {
      setToast({
        isOpen: true,
        message: `El item excede el saldo pendiente. Saldo disponible: ${formatCurrency(pendingAmount)}`,
        type: 'error'
      });
      return;
    }

    setLoadingAction(true);
    try {
      // Combinar fecha y hora
      let itemDateTime = null;
      if (itemForm.item_date) {
        if (itemForm.item_time) {
          itemDateTime = `${itemForm.item_date} ${itemForm.item_time}:00`;
        } else {
          itemDateTime = `${itemForm.item_date} 00:00:00`;
        }
      }
      
      const submitData = {
        item_type: itemForm.item_type,
        product_id: itemForm.item_type === 'product' ? parseInt(itemForm.product_id) : null,
        service_id: itemForm.item_type === 'service' ? parseInt(itemForm.service_id) : null,
        description: itemForm.description || null,
        project_id: itemForm.project_id ? parseInt(itemForm.project_id) : null,
        item_date: itemDateTime,
        quantity: parseFloat(itemForm.quantity),
        unit_price: parseFloat(itemForm.unit_price),
        has_igv: itemForm.has_igv,
        unit_of_measure: itemForm.unit_of_measure || null,
        is_direct_consumption: itemForm.is_direct_consumption || false,
        warehouse_id: itemForm.warehouse_id ? parseInt(itemForm.warehouse_id) : null,
        ...(canShowMachinerySection && pendingMachineryEvent ? { machinery_events: [pendingMachineryEvent] } : {}),
      };

      let response;
      if (selectedItem) {
        response = await supplyContinuousDocumentService.updateItem(id, selectedItem.id, submitData);
      } else {
        response = await supplyContinuousDocumentService.addItem(id, submitData);
      }

      if (response.success) {
        setToast({
          isOpen: true,
          message: selectedItem 
            ? 'Item actualizado exitosamente'
            : 'Item agregado exitosamente',
          type: 'success'
        });
        setIsAddItemModalOpen(false);
        setIsEditItemModalOpen(false);
        setSelectedItem(null);
        // Recargar documento para tener guías generadas (entry_guide_item.entry_guide) en cada ítem
        loadDocument();
      } else {
        setItemErrors(response.errors || {});
        setToast({
          isOpen: true,
          message: response.message || 'Error al guardar el item',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al guardar item:', error);
      setToast({
        isOpen: true,
        message: 'Error al guardar el item',
        type: 'error'
      });
    } finally {
      setLoadingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <div className="text-gray-500">Cargando documento...</div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Documento no encontrado</p>
          <Link to="/documents" className="mt-4 inline-block text-teal-600 hover:text-teal-700">
            Volver a la lista
          </Link>
        </div>
      </div>
    );
  }

  const itemsTotal = getItemsTotal();
  const itemsSubtotal = getItemsSubtotal();
  const pendingAmount = getPendingAmount();
  const baseImponible = parseFloat(document?.base_imponible || 0);
  const noGravados = parseFloat(document?.no_gravados || 0);
  const expectedTotal = baseImponible + noGravados;
  const pendingPercentage = expectedTotal > 0 
    ? ((pendingAmount / expectedTotal) * 100).toFixed(1)
    : 100;
  const selectedProduct = itemForm.item_type === 'product'
    ? products.find((p) => p.id === parseInt(itemForm.product_id, 10))
    : null;
  const canShowMachinerySection = Boolean(
    itemForm.item_type === 'product' &&
    itemForm.is_direct_consumption &&
    selectedProduct?.machinery_register_enabled
  );

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Documento: {document.code || `DSC-${document.id}`}
              </h1>
              <p className="text-gray-600 mt-1">Agregue productos y servicios para cubrir el saldo del documento</p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/documents"
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors"
              >
                Volver
              </Link>
            </div>
          </div>
        </div>

        {/* Información General (cabeceras) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Información General</h2>
            {!isEditingHeader ? (
              <button
                type="button"
                onClick={() => {
                  const toInputDate = (d) => {
                    if (!d) return '';
                    const s = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10) : formatDateToLima(d);
                    return s;
                  };
                  const toNum = (v) => (v != null && v !== '') ? String(parseFloat(v)) : '';
                  const base = parseFloat(document.base_imponible) || 0;
                  const igv = parseFloat(document.igv_amount) || 0;
                  let igvPct = '18';
                  if (base > 0 && igv > 0) {
                    const pct = igv / base * 100;
                    if (Math.abs(pct - 18) < 1) igvPct = '18';
                    else if (Math.abs(pct - 10) < 1) igvPct = '10';
                    else igvPct = 'no_gravado';
                  } else if (igv === 0 && parseFloat(document.no_gravados) > 0) {
                    igvPct = 'no_gravado';
                  }
                  setHeaderForm({
                    issue_date: toInputDate(document.issue_date),
                    series: document.series || '',
                    number: document.number || '',
                    notes: document.notes || '',
                    base_imponible: toNum(document.base_imponible),
                    igv_percentage: igvPct,
                    no_gravados: toNum(document.no_gravados),
                  });
                  setIsEditingHeader(true);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors text-sm"
              >
                Editar
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={savingHeader}
                  onClick={async () => {
                    setSavingHeader(true);
                    try {
                      const payload = {
                        issue_date: headerForm.issue_date || undefined,
                        series: headerForm.series || undefined,
                        number: headerForm.number || undefined,
                        notes: headerForm.notes || undefined,
                      };
                      if (document.document_type === 'suministro_continuo') {
                        payload.base_imponible = baseNum;
                        payload.igv_amount = calculatedIgv;
                        payload.no_gravados = noGravadosNum;
                        payload.total_amount = calculatedTotal;
                        payload.igv_rate = igvPctValue === 'no_gravado' ? 0 : parseFloat(headerForm.igv_percentage);
                      }
                      const response = await supplyContinuousDocumentService.updateDocumentHeader(id, payload);
                      if (response.success) {
                        setDocument(response.data);
                        setItems(response.data.items || []);
                        setIsEditingHeader(false);
                        setToast({ isOpen: true, message: 'Información actualizada', type: 'success' });
                      } else {
                        setToast({ isOpen: true, message: response.message || 'Error al guardar', type: 'error' });
                      }
                    } catch (error) {
                      setToast({ isOpen: true, message: 'Error al actualizar la información', type: 'error' });
                    } finally {
                      setSavingHeader(false);
                    }
                  }}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors text-sm disabled:opacity-50"
                >
                  {savingHeader ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingHeader(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
          <div className="space-y-4">
            {/* Primera fila */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Proveedor</label>
                <p className="text-gray-900 font-medium">{document.supplier?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Fecha de Emisión</label>
                {isEditingHeader ? (
                  <input
                    type="date"
                    value={headerForm.issue_date}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, issue_date: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">
                    {document.issue_date ? formatDateLocaleLima(document.issue_date) : 'N/A'}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Serie</label>
                {isEditingHeader ? (
                  <input
                    type="text"
                    value={headerForm.series}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, series: e.target.value }))}
                    placeholder="Serie"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{document.series || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Número</label>
                {isEditingHeader ? (
                  <input
                    type="text"
                    value={headerForm.number}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, number: e.target.value }))}
                    placeholder="Número"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{document.number || 'N/A'}</p>
                )}
              </div>
            </div>
            {/* Segunda fila - Estado */}
            <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-600">Estado</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    document.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 
                    document.status === 'completed' ? 'bg-green-100 text-green-800' :
                    document.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {document.status === 'open' ? 'Abierto' : 
                     document.status === 'completed' ? 'Completado' : 
                     document.status === 'issued' ? 'Emitido' :
                     document.status}
                  </span>
                </div>
              </div>
              {isEditingHeader && (
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-600">Observaciones</label>
                  <input
                    type="text"
                    value={headerForm.notes}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Observaciones"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
              )}
              {!isEditingHeader && document.notes && (
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-600">Observaciones</label>
                  <p className="text-gray-900">{document.notes}</p>
                </div>
              )}
            </div>
            {/* Montos: en edición (suministro continuo) Base, % IGV y No Gravados editables; IGV y Total calculados */}
            <div className="flex items-center justify-between gap-6 flex-wrap pt-4 border-t border-gray-200">
              <div className="flex-1 min-w-[150px]">
                <label className="text-sm font-medium text-gray-600">Base Imponible</label>
                {isEditingHeader && document.document_type === 'suministro_continuo' ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={headerForm.base_imponible}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, base_imponible: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-gray-900">{formatCurrency(document.base_imponible || 0)}</p>
                    <span className="text-xs text-gray-500">({pendingPercentage}% pendiente)</span>
                  </div>
                )}
              </div>
              {isEditingHeader && document.document_type === 'suministro_continuo' && (
                <div className="flex-1 min-w-[140px]">
                  <label className="text-sm font-medium text-gray-600">% IGV</label>
                  <select
                    value={headerForm.igv_percentage}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, igv_percentage: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  >
                    <option value="18">18%</option>
                    <option value="10.5">10.5%</option>
                    <option value="10">10%</option>
                    <option value="no_gravado">No gravado</option>
                  </select>
                </div>
              )}
              <div className="flex-1 min-w-[150px]">
                <label className="text-sm font-medium text-gray-600">IGV</label>
                {isEditingHeader && document.document_type === 'suministro_continuo' ? (
                  <p className="text-gray-900 mt-1">{formatCurrency(calculatedIgv)}</p>
                ) : (
                  <p className="text-gray-900">{formatCurrency(displayIgv)}</p>
                )}
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="text-sm font-medium text-gray-600">No Gravados</label>
                {isEditingHeader && document.document_type === 'suministro_continuo' ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={headerForm.no_gravados}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, no_gravados: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{formatCurrency(document.no_gravados || 0)}</p>
                )}
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="text-sm font-medium text-gray-600">Monto Total</label>
                {isEditingHeader && document.document_type === 'suministro_continuo' ? (
                  <p className="text-gray-900 font-semibold text-lg mt-1">{formatCurrency(calculatedTotal)}</p>
                ) : (
                  <p className="text-gray-900 font-semibold text-lg">{formatCurrency(displayTotalDoc)}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Items Agregados */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Items Agregados</h2>
            {(document.status === 'open' || document.status === 'issued') && (
              <button
                onClick={handleAddItem}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors text-sm"
                disabled={pendingAmount <= 0.01}
              >
                + Agregar Item
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay items agregados. Agregue productos o servicios para cubrir el saldo del documento.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía generada</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha y Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGV</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    {(document.status === 'open' || document.status === 'issued') && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.item_type === 'product' ? 'Producto' : 'Servicio'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <p className="whitespace-nowrap">
                          {item.item_type === 'product'
                            ? `${item.product?.sku || ''} - ${item.product?.name || ''}`
                            : `${item.service?.sku || ''} - ${item.service?.name || ''}`
                          }
                        </p>
                        {getMachinerySummaryByItem(item.id) ? (
                          <p className="text-xs text-teal-700 mt-0.5 truncate max-w-[360px]" title={getMachinerySummaryByItem(item.id)}>
                            {getMachinerySummaryByItem(item.id)}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.entry_guide_item?.entry_guide?.code ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title="Se generó la guía para este ítem">
                            Se generó la guía: {item.entry_guide_item.entry_guide.code}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.item_date ? (
                          <>
                            {formatDateLocaleLima(item.item_date)} {formatTimeToLima(item.item_date)}
                          </>
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.quantity} {item.unit_of_measure || ''}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(item.unit_price)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(item.subtotal)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(item.tax_amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(item.total)}
                      </td>
                      {(document.status === 'open' || document.status === 'issued') && (
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEditItem(item)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Eliminar
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Saldo Pendiente - Abajo a la derecha */}
          <div className="mt-6 flex justify-end">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 min-w-[250px]">
              <p className="text-sm text-gray-600 mb-2">Saldo Pendiente</p>
              <p className={`text-2xl font-bold mb-1 ${pendingAmount > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(pendingAmount)}
              </p>
              <p className="text-xs text-gray-500">
                Base Imponible + No Gravados: {formatCurrency(expectedTotal)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Items agregados (subtotal): {formatCurrency(itemsSubtotal)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Agregar/Editar Item */}
      {(isAddItemModalOpen || isEditItemModalOpen) && (() => {
        const lineProduct =
          itemForm.item_type === 'product' && itemForm.product_id
            ? products.find((p) => String(p.id) === String(itemForm.product_id))
            : null;
        const totals = calculateItemTotals(
          itemForm.quantity,
          itemForm.unit_price,
          itemForm.has_igv,
          lineProduct,
          itemForm.unit_of_measure
        );
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {isEditItemModalOpen ? 'Editar Item' : 'Agregar Item'}
                </h3>

                <div className="mb-4">
                  <PricesIncludeIgvToggle
                    value={pricesIncludeIgv}
                    onChange={setPricesIncludeIgv}
                    igvRate={document?.igv_rate ?? 18}
                  />
                </div>
                
                <div className="space-y-4">
                  {/* Tipo de Item */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Item <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={itemForm.item_type}
                      onChange={(e) => {
                        setItemForm(prev => ({ 
                          ...prev, 
                          item_type: e.target.value,
                          product_id: '',
                          service_id: ''
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="product">Producto</option>
                      <option value="service">Servicio</option>
                    </select>
                  </div>

                  {/* Producto o Servicio */}
                  {itemForm.item_type === 'product' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Producto <span className="text-red-500">*</span>
                      </label>
                      <ProductSelector
                        value={itemForm.product_id}
                        onChange={(value) => {
                          const product = products.find(p => p.id === parseInt(value));
                          setItemForm(prev => ({ 
                            ...prev, 
                            product_id: value,
                            // Cargar automáticamente la unidad de medida del producto si tiene
                            unit_of_measure: product?.unit_of_measure || prev.unit_of_measure || ''
                          }));
                        }}
                        products={products}
                        error={itemErrors.product_id}
                      />
                      {itemErrors.product_id && (
                        <p className="mt-1 text-sm text-red-600">{itemErrors.product_id}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Servicio <span className="text-red-500">*</span>
                      </label>
                      <ServiceSelector
                        value={itemForm.service_id}
                        onChange={(value) => setItemForm(prev => ({ ...prev, service_id: value }))}
                        services={services}
                        error={itemErrors.service_id}
                        showSku={true}
                      />
                      {itemErrors.service_id && (
                        <p className="mt-1 text-sm text-red-600">{itemErrors.service_id}</p>
                      )}
                    </div>
                  )}

                  {/* Fecha y Hora */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={itemForm.item_date}
                        onChange={(e) => setItemForm(prev => ({ ...prev, item_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hora <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={itemForm.item_time}
                        onChange={(e) => setItemForm(prev => ({ ...prev, item_time: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Proyecto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Proyecto
                    </label>
                    <select
                      value={itemForm.project_id}
                      onChange={(e) => setItemForm(prev => ({ ...prev, project_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Seleccionar proyecto (opcional)</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.code} - {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Descripción */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripción
                    </label>
                    <textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Descripción del item (opcional)..."
                    />
                  </div>

                  {/* Cantidad y Precio Unitario */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cantidad <span className="text-red-500">*</span>
                      </label>
                      <QuantityTextInput
                        value={itemForm.quantity}
                        onChange={(qty) => setItemForm((prev) => ({ ...prev, quantity: qty }))}
                        min={0.001}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          itemErrors.quantity ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="0"
                      />
                      {itemErrors.quantity && (
                        <p className="mt-1 text-sm text-red-600">{itemErrors.quantity}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precio Unitario <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">S/</span>
                        <UnitPriceTextInput
                          netPrice={itemForm.unit_price}
                          onNetPriceChange={(net) => setItemForm((prev) => ({ ...prev, unit_price: net }))}
                          pricesIncludeIgv={pricesIncludeIgv}
                          hasIgv={itemForm.has_igv}
                          igvRate={document?.igv_rate ?? 18}
                          className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                            itemErrors.unit_price ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0.0000"
                        />
                      </div>
                      <div className="min-h-[14px] mt-1 text-[10px] leading-[14px]">
                        <PriceIgvHint
                          netPrice={itemForm.unit_price}
                          igvRate={document?.igv_rate ?? 18}
                          hasIgv={itemForm.has_igv}
                          pricesIncludeIgv={pricesIncludeIgv}
                        />
                      </div>
                      {itemErrors.unit_price && (
                        <p className="mt-1 text-sm text-red-600">{itemErrors.unit_price}</p>
                      )}
                    </div>
                  </div>

                  {/* Unidad de Medida (solo para productos) */}
                  {itemForm.item_type === 'product' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unidad de Medida
                      </label>
                      <UnitSelect
                        value={itemForm.unit_of_measure}
                        onChange={(v) => setItemForm((prev) => ({ ...prev, unit_of_measure: v }))}
                        units={units}
                        product={products.find((p) => String(p.id) === String(itemForm.product_id))}
                        emptyLabel="Seleccionar unidad de medida (opcional)"
                        showCodeOnly={false}
                      />
                    </div>
                  )}

                  {/* ¿Tiene IGV? */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ¿Tiene IGV?
                    </label>
                    <select
                      value={itemForm.has_igv ? 'true' : 'false'}
                      onChange={(e) => setItemForm(prev => ({ ...prev, has_igv: e.target.value === 'true' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  {/* Consumo Directo y Almacén (solo para productos) */}
                  {itemForm.item_type === 'product' && (
                    <>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={itemForm.is_direct_consumption}
                            onChange={(e) => {
                              setItemForm(prev => ({ ...prev, is_direct_consumption: e.target.checked, warehouse_id: e.target.checked ? '' : prev.warehouse_id }));
                              if (itemErrors.warehouse_id) setItemErrors(prev => ({ ...prev, warehouse_id: undefined }));
                            }}
                            className="mr-2 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Consumo Directo</span>
                        </label>
                      </div>

                      {!itemForm.is_direct_consumption && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Almacén <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={itemForm.warehouse_id}
                            onChange={(e) => {
                              setItemForm(prev => ({ ...prev, warehouse_id: e.target.value }));
                              if (itemErrors.warehouse_id) setItemErrors(prev => ({ ...prev, warehouse_id: undefined }));
                            }}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${itemErrors.warehouse_id ? 'border-red-500' : 'border-gray-300'}`}
                          >
                            <option value="">Seleccionar almacén</option>
                            {warehouses.map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.code} - {warehouse.name}
                              </option>
                            ))}
                          </select>
                          {itemErrors.warehouse_id && (
                            <p className="mt-1 text-sm text-red-600">{itemErrors.warehouse_id}</p>
                          )}
                        </div>
                      )}

                      {canShowMachinerySection && (
                        <div className="rounded-lg border border-teal-200 bg-teal-50 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-teal-800">Registro de vehículo/maquinaria</p>
                              <p className="text-xs text-teal-700 mt-1">
                                {pendingMachineryEvent?.displaySummary || 'Aun no se registró un evento.'}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIsMachineryEventModalOpen(true)}
                              className="px-3 py-1.5 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-md"
                            >
                              {pendingMachineryEvent ? 'Editar registro' : 'Registrar vehículo'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Totales calculados */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <label className="text-gray-600">Subtotal</label>
                        <p className="font-medium text-gray-900">{formatCurrency(totals.subtotal)}</p>
                      </div>
                      <div>
                        <label className="text-gray-600">IGV ({totals.taxRate}%)</label>
                        <p className="font-medium text-gray-900">{formatCurrency(totals.taxAmount)}</p>
                      </div>
                      <div>
                        <label className="text-gray-600">Total</label>
                        <p className="font-bold text-teal-700 text-lg">{formatCurrency(totals.total)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddItemModalOpen(false);
                      setIsEditItemModalOpen(false);
                      setSelectedItem(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                    disabled={loadingAction}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveItem}
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors disabled:opacity-50"
                    disabled={loadingAction}
                  >
                    {loadingAction ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={isDeleteItemModalOpen}
        onClose={() => {
          setIsDeleteItemModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDeleteItem}
        title="Eliminar Item"
        message={`¿Estás seguro de que deseas eliminar este item?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        loading={loadingAction}
      />

      <MachineryEventModal
        isOpen={isMachineryEventModalOpen}
        onClose={() => setIsMachineryEventModalOpen(false)}
        onSuccess={(eventData) => setPendingMachineryEvent(eventData)}
        collectOnly={true}
        productName={selectedProduct?.name || ''}
        sourceEventDate={itemForm.item_date || null}
        sourceEventTime={itemForm.item_time || null}
        sourceProjectId={itemForm.project_id || null}
        initialFormData={pendingMachineryEvent}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default SupplyContinuousDocumentDetail;
